import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DemoExportGate } from "@/components/DemoExportGate";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GitCompare, TrendingUp, TrendingDown, Minus, AlertTriangle, Download, Loader2, Save, BookmarkCheck, Trash2, FolderOpen, RefreshCw, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ClauseRef {
  contractClauseId: number;
  clauseId: number;
  clauseTitle: string;
  clauseCategory: string;
  versionNumber: number;
  versionId: number;
  hasOverride: boolean;
  isLocked: boolean;
}

interface ContractSummary {
  contractId: number;
  coachName: string;
  sport: string;
  contractType: string;
  status: string;
  startDate: string;
  endDate: string;
  termYears: number;
  annualSalary: number | null;
  signingBonus: number | null;
  buyoutClause: number | null;
  bonusTotal: number | null;
  allowanceTotal: number | null;
  totalComp: number | null;
  clauses: ClauseRef[];
  financialsMasked: boolean;
}

interface ClauseDiff {
  onlyInBase: ClauseRef[];
  onlyInCompare: ClauseRef[];
  inBoth: {
    clauseId: number;
    clauseTitle: string;
    clauseCategory: string;
    baseVersionNumber: number;
    compareVersionNumber: number;
    versionDiffers: boolean;
    baseHasOverride: boolean;
    compareHasOverride: boolean;
  }[];
}

interface CompareResult {
  base: ContractSummary;
  compare: ContractSummary;
  clauseDiff: ClauseDiff;
}

interface ContractOption {
  id: number;
  label: string;
  coachName: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  compensation: "Compensation", termination: "Termination", buyout: "Buyout",
  nil: "NIL", title_ix: "Title IX", indemnification: "Indemnification",
  governing_law: "Governing Law", outside_income: "Outside Income", general: "General",
};

const CATEGORY_COLORS: Record<string, string> = {
  compensation: "bg-emerald-50 text-emerald-700 border-emerald-200",
  termination: "bg-red-50 text-red-700 border-red-200",
  buyout: "bg-orange-50 text-orange-700 border-orange-200",
  nil: "bg-purple-50 text-purple-700 border-purple-200",
  title_ix: "bg-blue-50 text-blue-700 border-blue-200",
  indemnification: "bg-slate-50 text-slate-700 border-slate-200",
  governing_law: "bg-gray-50 text-gray-700 border-gray-200",
  outside_income: "bg-yellow-50 text-yellow-700 border-yellow-200",
  general: "bg-muted text-muted-foreground border-border",
};

function fmt(n: number | null) {
  if (n === null) return "—";
  return "$" + n.toLocaleString();
}

function DeltaBadge({ base, compare }: { base: number | null; compare: number | null }) {
  if (base === null || compare === null) return null;
  const delta = compare - base;
  if (Math.abs(delta) < 1) return <span className="text-xs text-muted-foreground ml-1">no change</span>;
  const pct = ((delta / base) * 100).toFixed(1);
  if (delta > 0) {
    return (
      <span className="flex items-center gap-0.5 text-xs text-green-700 font-medium ml-1">
        <TrendingUp className="h-3 w-3" /> +{fmt(delta)} (+{pct}%)
      </span>
    );
  }
  return (
    <span className="flex items-center gap-0.5 text-xs text-red-700 font-medium ml-1">
      <TrendingDown className="h-3 w-3" /> {fmt(delta)} ({pct}%)
    </span>
  );
}

function NumericRow({ label, base, compare }: { label: string; base: number | null; compare: number | null }) {
  const differs = base !== null && compare !== null && Math.abs(base - compare) >= 1;
  return (
    <div className={`grid grid-cols-[1fr_1fr_1fr] gap-2 py-2.5 text-sm border-b last:border-0 ${differs ? "bg-amber-50/40" : ""}`}>
      <div className="text-muted-foreground font-medium pl-2">{label}</div>
      <div className="text-center font-mono">{fmt(base)}</div>
      <div className={`text-center font-mono ${differs ? "font-semibold text-foreground" : ""}`}>{fmt(compare)}</div>
    </div>
  );
}

function TermRow({ base, compare }: { base: ContractSummary; compare: ContractSummary }) {
  const bdiff = base.termYears !== compare.termYears;
  return (
    <div className={`grid grid-cols-[1fr_1fr_1fr] gap-2 py-2.5 text-sm border-b ${bdiff ? "bg-amber-50/40" : ""}`}>
      <div className="text-muted-foreground font-medium pl-2">Term length</div>
      <div className="text-center">{base.termYears}y</div>
      <div className={`text-center ${bdiff ? "font-semibold" : ""}`}>{compare.termYears}y</div>
    </div>
  );
}

interface SnapshotListItem {
  id: number;
  baseContractId: number;
  compareContractId: number;
  label: string | null;
  createdAt: string;
  createdByUserId: string | null;
  baseContractUpdatedAt: string | null;
  compareContractUpdatedAt: string | null;
  isStale: boolean;
}

interface ContractCompareProps {
  contractId: number;
  isAdmin: boolean;
}

export function ContractCompare({ contractId, isAdmin }: ContractCompareProps) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [allContracts, setAllContracts] = useState<ContractOption[]>([]);
  const [selectedCompareId, setSelectedCompareId] = useState<string>("");
  const [result, setResult] = useState<CompareResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(true);
  const [snapshots, setSnapshots] = useState<SnapshotListItem[]>([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [savedSnapshotId, setSavedSnapshotId] = useState<number | null>(null);
  const [snapshotLabel, setSnapshotLabel] = useState("");
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [openedSnapshotMeta, setOpenedSnapshotMeta] = useState<{
    isStale: boolean;
    baseContractUpdatedAt: string | null;
    compareContractUpdatedAt: string | null;
    snapshotCreatedAt: string;
    baseContractId: number;
    compareContractId: number;
  } | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadSnapshots = useCallback(async () => {
    setLoadingSnapshots(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/compare-snapshots?contractId=${contractId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json() as SnapshotListItem[];
        setSnapshots(Array.isArray(data) ? data : []);
      }
    } catch {
    } finally {
      setLoadingSnapshots(false);
    }
  }, [contractId, getToken]);

  useEffect(() => { void loadSnapshots(); }, [loadSnapshots]);

  useEffect(() => {
    const load = async () => {
      setLoadingContracts(true);
      try {
        const token = await getToken();
        const res = await fetch("/api/contracts?limit=200", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json() as { id: number; coachName?: string; sport?: string; status?: string }[];
          const contracts = Array.isArray(data) ? data : [];
          setAllContracts(
            contracts
              .filter((c) => c.id !== contractId)
              .map((c) => ({
                id: c.id,
                coachName: c.coachName ?? "Unknown",
                label: `${c.coachName ?? "Unknown"} — ${c.sport ?? ""} (${c.status ?? ""}) #${c.id}`,
              }))
          );
        }
      } catch {
      } finally {
        setLoadingContracts(false);
      }
    };
    void load();
  }, [contractId, getToken]);

  const handleCompare = async () => {
    if (!selectedCompareId) return;
    setLoading(true);
    setResult(null);
    setSavedSnapshotId(null);
    setSnapshotLabel("");
    setOpenedSnapshotMeta(null);
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/compare?with=${selectedCompareId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Compare failed");
      }
      setResult(await res.json() as CompareResult);
    } catch (err) {
      toast({ title: "Compare failed", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSnapshot = async () => {
    if (!result || !selectedCompareId) return;
    setSavingSnapshot(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/compare-snapshots", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          baseContractId: contractId,
          compareContractId: parseInt(selectedCompareId, 10),
          label: snapshotLabel.trim() || null,
          result,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Save failed");
      }
      const saved = await res.json() as SnapshotListItem;
      setSavedSnapshotId(saved.id);
      toast({ title: "Comparison saved", description: saved.label ?? `Snapshot #${saved.id}` });
      void loadSnapshots();
    } catch (err) {
      toast({ title: "Save failed", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
    } finally {
      setSavingSnapshot(false);
    }
  };

  const handleOpenSnapshot = async (snapshotId: number) => {
    setLoading(true);
    setResult(null);
    try {
      const token = await getToken();
      const res = await fetch(`/api/compare-snapshots/${snapshotId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Open failed");
      }
      const snap = await res.json() as {
        id: number;
        label: string | null;
        result: CompareResult;
        baseContractId: number;
        compareContractId: number;
        createdAt: string;
        baseContractUpdatedAt: string | null;
        compareContractUpdatedAt: string | null;
        isStale: boolean;
      };
      setResult(snap.result);
      setSelectedCompareId(String(
        snap.baseContractId === contractId ? snap.compareContractId : snap.baseContractId
      ));
      setSavedSnapshotId(snap.id);
      setSnapshotLabel(snap.label ?? "");
      setOpenedSnapshotMeta({
        isStale: snap.isStale,
        baseContractUpdatedAt: snap.baseContractUpdatedAt,
        compareContractUpdatedAt: snap.compareContractUpdatedAt,
        snapshotCreatedAt: snap.createdAt,
        baseContractId: snap.baseContractId,
        compareContractId: snap.compareContractId,
      });
    } catch (err) {
      toast({ title: "Could not open snapshot", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshSnapshot = async (overwrite: boolean) => {
    if (!savedSnapshotId || !openedSnapshotMeta) return;
    setRefreshing(true);
    try {
      const token = await getToken();
      // Use the snapshot's original orientation, not the current page's contractId,
      // so the live result preserves the same base/compare semantics as the stored
      // snapshot. Otherwise overwriting could invert what "onlyInBase" means.
      const baseId = openedSnapshotMeta.baseContractId;
      const compareId = openedSnapshotMeta.compareContractId;
      const compareRes = await fetch(`/api/contracts/${baseId}/compare?with=${compareId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!compareRes.ok) {
        const err = await compareRes.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Refresh failed");
      }
      const fresh = await compareRes.json() as CompareResult;
      setResult(fresh);

      if (overwrite) {
        const patchRes = await fetch(`/api/compare-snapshots/${savedSnapshotId}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ result: fresh }),
        });
        if (!patchRes.ok) {
          const err = await patchRes.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error ?? "Overwrite failed");
        }
        const updated = await patchRes.json() as {
          createdAt: string;
          baseContractId: number;
          compareContractId: number;
          baseContractUpdatedAt: string | null;
          compareContractUpdatedAt: string | null;
          isStale: boolean;
        };
        setOpenedSnapshotMeta({
          isStale: updated.isStale,
          baseContractUpdatedAt: updated.baseContractUpdatedAt,
          compareContractUpdatedAt: updated.compareContractUpdatedAt,
          snapshotCreatedAt: updated.createdAt,
          baseContractId: updated.baseContractId,
          compareContractId: updated.compareContractId,
        });
        toast({ title: "Snapshot refreshed", description: "Saved comparison updated with the latest data." });
        void loadSnapshots();
      } else {
        toast({ title: "Showing live comparison", description: "Saved snapshot left unchanged." });
      }
    } catch (err) {
      toast({ title: "Refresh failed", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteSnapshot = async (snapshotId: number) => {
    try {
      const token = await getToken();
      const res = await fetch(`/api/compare-snapshots/${snapshotId}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok && res.status !== 204) throw new Error("Delete failed");
      if (savedSnapshotId === snapshotId) setSavedSnapshotId(null);
      void loadSnapshots();
    } catch (err) {
      toast({ title: "Delete failed", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
    }
  };

  const handleDownloadAudit = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/clause-audit.pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clause-audit-${contractId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Export failed", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <GitCompare className="h-5 w-5 text-muted-foreground" />
              Contract Comparison
            </CardTitle>
            {isAdmin && (
              <DemoExportGate label="Clause Audit PDF" size="sm" variant="outline">
                <Button variant="outline" size="sm" onClick={handleDownloadAudit}>
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Clause Audit PDF
                </Button>
              </DemoExportGate>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              {loadingContracts ? (
                <div className="h-9 rounded-md border bg-muted/30 flex items-center px-3 text-sm text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Loading contracts…
                </div>
              ) : (
                <Select value={selectedCompareId} onValueChange={setSelectedCompareId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a contract to compare against…" />
                  </SelectTrigger>
                  <SelectContent>
                    {allContracts.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Button onClick={handleCompare} disabled={!selectedCompareId || loading}>
              {loading ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Comparing…</> : <><GitCompare className="h-3.5 w-3.5 mr-1.5" /> Compare</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
            Saved Comparisons
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingSnapshots ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading saved comparisons…
            </div>
          ) : snapshots.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              No saved comparisons for this contract yet. Run a comparison and save it for later reference.
            </div>
          ) : (
            <div className="space-y-1.5">
              {snapshots.map((s) => {
                const otherId = s.baseContractId === contractId ? s.compareContractId : s.baseContractId;
                const otherCoachName =
                  allContracts.find((c) => c.id === otherId)?.coachName ?? "Contract";
                const dt = new Date(s.createdAt);
                return (
                  <div
                    key={s.id}
                    data-testid={`snapshot-row-${s.id}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${savedSnapshotId === s.id ? "bg-amber-50/60 border-amber-200" : "bg-background"}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate flex items-center gap-2">
                        <span className="truncate">{s.label || `Snapshot #${s.id}`}</span>
                        {savedSnapshotId === s.id && (
                          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                            <BookmarkCheck className="h-3 w-3 mr-1" /> open
                          </Badge>
                        )}
                        {s.isStale && (
                          <Badge
                            variant="outline"
                            className="text-xs bg-orange-50 text-orange-700 border-orange-200"
                            data-testid={`snapshot-stale-badge-${s.id}`}
                          >
                            <Clock className="h-3 w-3 mr-1" /> out of date
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground" data-testid={`snapshot-row-meta-${s.id}`}>
                        vs <span className="font-medium text-foreground/80">{otherCoachName}</span>
                        <span className="font-mono ml-1 text-muted-foreground/70" title={`Contract ID #${otherId}`}>
                          #{otherId}
                        </span>
                        <span> · saved {dt.toLocaleString()}</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenSnapshot(s.id)}
                      data-testid={`button-open-snapshot-${s.id}`}
                    >
                      Open
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteSnapshot(s.id)}
                      aria-label="Delete snapshot"
                      data-testid={`button-delete-snapshot-${s.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {result && openedSnapshotMeta?.isStale && (
        <Card
          className="border-orange-300 bg-orange-50/60"
          data-testid="snapshot-stale-banner"
        >
          <CardContent className="py-3">
            <div className="flex items-start gap-3 flex-wrap">
              <AlertTriangle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-semibold text-orange-900">
                  This saved comparison is out of date
                </div>
                <div className="text-orange-800/90 mt-0.5 text-xs">
                  Saved {new Date(openedSnapshotMeta.snapshotCreatedAt).toLocaleString()}.
                  {openedSnapshotMeta.baseContractUpdatedAt &&
                    new Date(openedSnapshotMeta.baseContractUpdatedAt) >
                      new Date(openedSnapshotMeta.snapshotCreatedAt) && (
                      <> This contract was updated {new Date(openedSnapshotMeta.baseContractUpdatedAt).toLocaleString()}.</>
                    )}
                  {openedSnapshotMeta.compareContractUpdatedAt &&
                    new Date(openedSnapshotMeta.compareContractUpdatedAt) >
                      new Date(openedSnapshotMeta.snapshotCreatedAt) && (
                      <> The comparison contract was updated {new Date(openedSnapshotMeta.compareContractUpdatedAt).toLocaleString()}.</>
                    )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRefreshSnapshot(false)}
                  disabled={refreshing}
                  data-testid="button-refresh-snapshot-live"
                >
                  {refreshing ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Refreshing…</>
                  ) : (
                    <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh (live only)</>
                  )}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleRefreshSnapshot(true)}
                  disabled={refreshing}
                  data-testid="button-refresh-snapshot-overwrite"
                >
                  {refreshing ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Refreshing…</>
                  ) : (
                    <><Save className="h-3.5 w-3.5 mr-1.5" /> Refresh & overwrite</>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {result && (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-base">Financial Comparison</CardTitle>
                <div className="flex items-center gap-2">
                  <Input
                    value={snapshotLabel}
                    onChange={(e) => setSnapshotLabel(e.target.value)}
                    placeholder="Label (optional)"
                    className="h-8 w-48 text-sm"
                    maxLength={200}
                    data-testid="input-snapshot-label"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSaveSnapshot}
                    disabled={savingSnapshot}
                    data-testid="button-save-snapshot"
                  >
                    {savingSnapshot ? (
                      <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Saving…</>
                    ) : savedSnapshotId !== null ? (
                      <><BookmarkCheck className="h-3.5 w-3.5 mr-1.5" /> Save again</>
                    ) : (
                      <><Save className="h-3.5 w-3.5 mr-1.5" /> Save comparison</>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 px-2 py-2 bg-muted/50 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b">
                <div className="pl-2">Field</div>
                <div className="text-center truncate" title={result.base.coachName}>This contract</div>
                <div className="text-center truncate" title={result.compare.coachName}>
                  {result.compare.coachName} #{result.compare.contractId}
                </div>
              </div>
              {result.base.financialsMasked ? (
                <div className="py-6 text-center text-sm text-muted-foreground italic">Financial details are restricted for your role.</div>
              ) : (
                <div className="px-2">
                  <NumericRow label="Annual Salary" base={result.base.annualSalary} compare={result.compare.annualSalary} />
                  <NumericRow label="Signing Bonus" base={result.base.signingBonus} compare={result.compare.signingBonus} />
                  <NumericRow label="Buyout Clause" base={result.base.buyoutClause} compare={result.compare.buyoutClause} />
                  <NumericRow label="Total Bonuses" base={result.base.bonusTotal} compare={result.compare.bonusTotal} />
                  <NumericRow label="Total Allowances" base={result.base.allowanceTotal} compare={result.compare.allowanceTotal} />
                  <NumericRow label="Total Compensation" base={result.base.totalComp} compare={result.compare.totalComp} />
                  <TermRow base={result.base} compare={result.compare} />
                  {result.base.annualSalary !== null && result.compare.annualSalary !== null && (
                    <div className="flex items-center gap-2 py-3 pl-2 text-sm border-t">
                      <span className="text-muted-foreground">Salary delta:</span>
                      <DeltaBadge base={result.base.annualSalary} compare={result.compare.annualSalary} />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Clause Differences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {result.clauseDiff.onlyInBase.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Only in this contract ({result.clauseDiff.onlyInBase.length})
                  </div>
                  <div className="space-y-1">
                    {result.clauseDiff.onlyInBase.map((c) => (
                      <div key={c.clauseId} className="flex items-center gap-2 text-sm py-1.5 px-2 rounded-md bg-blue-50 border border-blue-100">
                        <Minus className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                        <span className="flex-1">{c.clauseTitle}</span>
                        <Badge variant="outline" className={`text-xs ${CATEGORY_COLORS[c.clauseCategory] ?? ""}`}>
                          {CATEGORY_LABELS[c.clauseCategory] ?? c.clauseCategory}
                        </Badge>
                        <span className="text-xs text-muted-foreground">v{c.versionNumber}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.clauseDiff.onlyInCompare.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Only in comparison contract ({result.clauseDiff.onlyInCompare.length})
                  </div>
                  <div className="space-y-1">
                    {result.clauseDiff.onlyInCompare.map((c) => (
                      <div key={c.clauseId} className="flex items-center gap-2 text-sm py-1.5 px-2 rounded-md bg-muted border">
                        <Minus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="flex-1">{c.clauseTitle}</span>
                        <Badge variant="outline" className={`text-xs ${CATEGORY_COLORS[c.clauseCategory] ?? ""}`}>
                          {CATEGORY_LABELS[c.clauseCategory] ?? c.clauseCategory}
                        </Badge>
                        <span className="text-xs text-muted-foreground">v{c.versionNumber}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.clauseDiff.inBoth.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    In both contracts ({result.clauseDiff.inBoth.length})
                  </div>
                  <div className="space-y-1">
                    {result.clauseDiff.inBoth.map((c) => {
                      const hasChange = c.versionDiffers || (c.baseHasOverride !== c.compareHasOverride);
                      return (
                        <div key={c.clauseId} className={`flex items-center gap-2 text-sm py-1.5 px-2 rounded-md border ${hasChange ? "bg-amber-50 border-amber-200" : "bg-background"}`}>
                          {hasChange ? (
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                          ) : (
                            <div className="h-3.5 w-3.5 shrink-0 rounded-full bg-green-200" />
                          )}
                          <span className="flex-1">{c.clauseTitle}</span>
                          <Badge variant="outline" className={`text-xs ${CATEGORY_COLORS[c.clauseCategory] ?? ""}`}>
                            {CATEGORY_LABELS[c.clauseCategory] ?? c.clauseCategory}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {c.versionDiffers
                              ? `v${c.baseVersionNumber} → v${c.compareVersionNumber}`
                              : `v${c.baseVersionNumber}`}
                          </span>
                          {c.baseHasOverride && !c.compareHasOverride && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">this: overridden</Badge>
                          )}
                          {!c.baseHasOverride && c.compareHasOverride && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">other: overridden</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {result.clauseDiff.onlyInBase.length === 0 && result.clauseDiff.onlyInCompare.length === 0 && result.clauseDiff.inBoth.filter((c) => c.versionDiffers).length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-4">
                  Both contracts share the same clause set with no version differences.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
